import React from 'react';
import styled from 'styled-components';
import classNames from 'classnames';

// components
import { NavLink } from 'react-router-dom';
import { BoxProps, Flex, Section, SpacingProps } from '../../box';
import { IconAnimatedChevron } from '../../icon';
import ScrollableContainerComponent from '../../scrollable-container';
import { ReactScrollContainer, ReactScrollLink } from '../../react-scroll';
import { H1, H2, H3, TitleProps } from '../../title';

// constants
import layoutConstants from '../layout-constants';

// styling
import { colors, deviceSizes, spacing } from '../../../style';
import { actionColor } from '../../../style/theme-helpers';
import { withSpacing } from '../../../utils/with-spacing';

/****************
  Body
 ****************/

/****************
  Content
 ****************/

export const FullHeightContainer = styled(Section)<BoxProps>`
  height: 100%;
`;

export const PageContainer = ({ ...rest }: BoxProps) => <Flex componentClass={FullHeightContainer} {...rest} />;

export const ScrollableSection = styled(({ ...rest }) => (
  <ScrollableContainerComponent as={ReactScrollContainer} {...rest} />
))`
  height: 100%;
  flex-grow: 1;
  padding: ${spacing.xl};

  @media (max-width: ${deviceSizes.sm}) {
    padding: 24px;
  }
`;

export const InnerContainer = styled(Section)<BoxProps>`
  max-width: ${layoutConstants.innerContainerWidth};
`;

/****************
  Navigation
 ****************/

export const MenuButtonsContainer = ({ ...rest }: BoxProps) => <Flex flexDirection={'column'} flexGrow={1} {...rest} />;

export const SectionButton = styled(({ ...rest }) => {
  return <ReactScrollLink spy={true} smooth={true} delay={0} activeClass="active" href={rest?.to} {...rest} />;
})`
  font-size: 1rem;
  display: flex;
  width: 100%;
  margin-bottom: 8px;
  border-radius: 4px;
  padding: 16px;
  align-items: center;
  justify-content: flex-start;
  font-weight: 500;
  cursor: pointer;
  transition: 0.2s;
  color: ${colors.gray900};

  &:hover {
    background: ${colors.gray100};
  }

  &:active {
    background: ${colors.gray300};
  }

  &.active {
    color: ${props => props.theme.actionColor};
    background: ${props => props.theme.actionLight};
  }

  &.active {
    font-weight: 500;
  }

  &:focus-visible {
    border: 2px solid ${colors.sky};
  }

  &.disabled, &[disabled] {
    background: ${colors.gray600};
    cursor: not-allowed;
  }
}
`;

export const SideMenuButton = styled(
  ({
    label,
    disabled,
    onClick,
    className,
    active,
    isOpen: isOpenProp = true,
    children,
    disableRoute,
    componentClass: Component = NavLink,
    to,
    decoration,
    exact,
    ...rest
  }: {
    label?: string;
    exact?: boolean;
    disabled?: boolean;
    onClick?: (e: React.MouseEvent<HTMLElement>) => void;
    isOpen?: boolean;
    disableRoute?: boolean;
    children?: React.ReactNode;
    active?: boolean;
    className?: string;
    componentClass?: 'button' | React.ElementType;
    to?: string;
    decoration?: TitleProps['decoration'];
  } & SpacingProps) => {
    const [isOpen, setIsOpen] = React.useState(isOpenProp);

    return (
      <React.Fragment>
        <Component
          exact={exact}
          aria-expanded={!children ? undefined : isOpen}
          disabled={disabled}
          className={classNames(className, { active })}
          onClick={(e: any) => {
            if (disableRoute) {
              e.preventDefault();
            }
            if (!disabled) {
              onClick?.(e);
            }
            setIsOpen(!isOpen);
          }}
          to={to}
          {...rest}
        >
          <H2 size={'sm'} marginBottom={'none'} variant={'inherit'} decoration={decoration}>
            {label}
          </H2>
          {!disabled && children && <IconAnimatedChevron size={'xl'} isOn={isOpen} />}
        </Component>
        {isOpen ? children : null}
      </React.Fragment>
    );
  },
)`
  padding: 16px 8px 16px 16px;
  ${withSpacing};
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: ${actionColor};

  &:hover {
    background: ${colors.gray100};
  }

  &.active {
    color: ${props => props.theme.actionColor};
    background: ${props => props.theme.actionLight};
  }

  &:focus-visible {
    border: 2px solid ${colors.sky};
  }

  &.disabled,
  &[disabled] {
    background: ${colors.gray600};
    color: ${colors.gray300};
    cursor: not-allowed;
  }
`;

export const SideMenuTabButton = styled(({ to, disabled, className, href, children, componentClass, ...rest }) => {
  const isLink = to || href;
  const Component = componentClass || (isLink ? NavLink : 'button');
  return (
    <Component
      to={to || href}
      activeClassName={isLink ? 'active' : undefined}
      className={className}
      disabled={disabled}
      {...rest}
    >
      <H3 weight={400} variant={'inherit'} size={'sm'}>
        {children}
      </H3>
    </Component>
  );
})`
  position: relative;
  display: flex;
  height: 48px;
  width: 100%;
  text-align: left;
  justify-content: flex-start;
  font-weight: 400;
  background: ${colors.white};
  text-decoration: none;
  align-items: center;
  border: none;
  background: transparent;
  padding: 16px;
  color: ${colors.gray900};
  border-radius: 8px;

  &:hover {
    background: ${colors.gray100};
  }

  &.active,
  &.active:hover {
    color: ${props => props.theme.actionColor};
  }

  &:not(:active)::before {
    content: '';
    position: absolute;
    left: ${spacing.xs};
    top: ${spacing.xs};
    height: 100%;
    border-left: 2px solid ${colors.gray100};
  }

  &:focus-visible {
    color: ${colors.sky};
  }

  &:last-child:not(:active)::before {
    height: calc(100% - ${spacing.xs});
  }

  & + &:not(:active)::before {
    top: ${spacing.none};
  }

  &:active::after,
  &.active::after {
    content: '';
    position: absolute;
    top: ${spacing.xs};
    left: ${spacing.xs};
    height: calc(100% - ${spacing.sm});
    border-left: 2px solid ${props => props.theme.actionColor};
    border-radius: 1px;
  }
`;

/****************
  Atoms
 ****************/

export const PageTitle = ({ ...rest }: TitleProps) => (
  <H1 size={'lg'} weight={500} marginBottom={'lg'} marginTop={'none'} {...rest} />
);

export const SecondaryPageTitle = ({ ...rest }: TitleProps) => (
  <H2 size={'lg'} weight={500} marginBottom={'lg'} marginTop={'none'} {...rest} />
);
